import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { BdsTableCell, BdsTableRow, BdsTypo } from 'blip-ds/dist/blip-ds-react';
import { trackEvent } from '../../../../api/Billing/Tracking';
import { getMonthTwoDigits } from '../../../../utils/date';
import { PageHeaderComponent } from '../../components/Metrics/PageHeader/PageHeaderComponent';
import AdvancedDailyEngagedUsersContext from '../../../../contexts/Billing/AdvancedDailyEngagedUsersContext';
import { AdvancedDailyEngagedUserMonthlyUsage } from '../../../../models/billing/advancedDailyEngagedUser/AdvancedDailyEngagedUserMonthlyUsage';
import { AdvancedDailyEngagedUserPaper } from '../styles';
import FeatureToggleService from '../../../../services/FeatureToggleService';
import { BillingMetrics } from '../../../../shared/BillingMetrics';
import { REPORT_PERIOD_SELECT, origin } from '../../../../shared/SegmentEvents';
import BillingDailyEngagedUsersServiceApi from '../../../../api/Billing/BillingAdvancedDailyEngagedUserApi';
import TableWithPagination from '../../../../components/TableWithPagination/TableWithPagination';
import { useSortedPaginatedData } from '../../../../hooks/useSortedPaginatedData';
import { DEFAULT_PAGE_OPTIONS, COLUMN_WIDTHS_5 as COLUMN_WIDTHS } from '../../../../utils/paginationUtils';
import { buildAdvancedDeuMetricIds, useDeuAiEnabled } from './helpers';

export interface AdvancedDailyEngagedUserMonthlyUsageProps {
  isDetailedView?: boolean;
}

const AdvancedDailyEngagedUserMonthlyUsageComponent: FC<AdvancedDailyEngagedUserMonthlyUsageProps> = ({ isDetailedView = false }) => {
  const { content, tenant, dateToSearch, loggedUser, trackingProps, getPlanStatus, periodOptions, setDateToSearch } =
    useContext(AdvancedDailyEngagedUsersContext);
  const isDeuAiEnabled = useDeuAiEnabled();

  const [chatbotData, setChatbotData] = useState<AdvancedDailyEngagedUserMonthlyUsage[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const billingDailyEngagedUsersApi = new BillingDailyEngagedUsersServiceApi();

  const { itemsPerPage, sortConfig, sortedData, paginatedData, handlePaginationChange, handleItemsPerPageChange, handleSortChange } =
    useSortedPaginatedData({ data: chatbotData });

  useEffect(() => {
    const getData = async () => {
      try {
        setIsLoading(true);
        const storageType = (await FeatureToggleService.isDatabricksStorageTypeEnabled(
          BillingMetrics.dailyEngagedUser,
        )) ? 1 : 0;

        const metricIds = buildAdvancedDeuMetricIds(isDeuAiEnabled);

        const monthString = `${dateToSearch.getFullYear()}-${getMonthTwoDigits(dateToSearch)}`;
        const data = await billingDailyEngagedUsersApi.getAdvancedDailyEngagedUserMonthlyUsage(
          tenant.info.id,
          metricIds,
          monthString,
          -1,
          storageType,
        );
        setChatbotData(data);
      } finally {
        setIsLoading(false);
      }
    };

    getData();
  }, [dateToSearch, isDeuAiEnabled]);

  const periodOptionChangeEvent = async (selectedOption: any) => {
    const dateToRetrieveData = new Date(selectedOption.detail.value);
    setDateToSearch(dateToRetrieveData);
    const planStatus = await getPlanStatus();
    trackEvent(REPORT_PERIOD_SELECT, tenant, loggedUser, {
      ...trackingProps,
      origin: origin.ADVANCEDDEUPERCHATBOT,
      date: dateToRetrieveData,
      planStatus,
    });
  };

  const tableRows = useMemo(
    () =>
      paginatedData.length > 0
        ? paginatedData.map((item) => (
            <BdsTableRow key={item.botName}>
              <BdsTableCell>
                <BdsTypo variant="fs-14" bold="bold" margin={false}>
                  {item.botName}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.dailyEngagedUserTotal.toLocaleString(content.locale)}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.prospectConversationTotal.toLocaleString(content.locale)}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {isDeuAiEnabled ? item.dailyEngagedUserAITotal.toLocaleString(content.locale) : item.userConversationTotal.toLocaleString(content.locale)}
                </BdsTypo>
              </BdsTableCell>
              <BdsTableCell>
                <BdsTypo variant="fs-14" margin={false}>
                  {item.activeMessageTotal.toLocaleString(content.locale)}
                </BdsTypo>
              </BdsTableCell>
            </BdsTableRow>
          ))
        : undefined,
    [paginatedData, content.locale, isDeuAiEnabled],
  );

  let headings = [
    content.chatbotConsumption.columnRouter,
    content.chatbotConsumption.columnDailyEngagedUsers,
    content.chatbotConsumption.columnProspectConversations,
  ];

  let columnKeys = ['botName', 'dailyEngagedUserTotal', 'prospectConversationTotal'];

  headings = isDeuAiEnabled ? [...headings, content.chatbotConsumption.columnAIAdditional] : [...headings, content.chatbotConsumption.columnUserConversations];
  columnKeys = isDeuAiEnabled ? [...columnKeys, 'dailyEngagedUserAITotal'] : [...columnKeys, 'userConversationTotal'];

  headings = [...headings, content.chatbotConsumption.columnActiveMessages];
  columnKeys = [...columnKeys, 'activeMessageTotal'];

  return (
    <>
      {isDetailedView && (
        <PageHeaderComponent
          title={content.chatbotConsumption.title}
          dateToSearch={dateToSearch}
          periodOptions={periodOptions}
          periodOptionChangeEvent={periodOptionChangeEvent}
        />
      )}
      <AdvancedDailyEngagedUserPaper>
        {!isDetailedView && (
          <BdsTypo variant="fs-24" bold="bold" margin={false}>
            {content.chatbotConsumption.title}
          </BdsTypo>
        )}
        <BdsTypo variant="fs-14" margin={false}>{content.chatbotConsumption.about}</BdsTypo>
        <TableWithPagination
          options={{
            heading: headings,
            columnKeys,
            columnWidths: COLUMN_WIDTHS,
            sortConfig,
            onSortChange: handleSortChange,
            itemsPerPageList: DEFAULT_PAGE_OPTIONS,
            itemsPerPage,
            numberOfItems: isLoading ? itemsPerPage : sortedData.length,
            isLoading,
            hasEmptyState: !isLoading && sortedData.length === 0,
            emptyStateSubtitle: '',
            isAutoHeight: true,
          }}
          onPaginationChange={handlePaginationChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        >
          {tableRows}
        </TableWithPagination>
      </AdvancedDailyEngagedUserPaper>
    </>
  );
};

export default AdvancedDailyEngagedUserMonthlyUsageComponent;
